/**
 * LED アンダーグロー（盤の下から陣地色の光が漏れる演出）の純粋計算 —
 * three.js / DOM 非依存（node でテスト可能）。
 *
 * ゲーミングキーボードの裏面 LED が机に落ちる光のイメージ:
 *   - 床グロー: 盤の真下に敷く大きな加算合成プレーン（先手ブルー / 後手レッド
 *     の 2 枚。中央で自然に混ざる）
 *   - 側面ストリップ: 盤土台の底辺 4 辺に沿う発光ライン（前後 = 各陣地色、
 *     左右 = 青→赤グラデーション）
 *   - 呼吸: 全体の明るさを周期 4 秒 ±15% でゆっくり揺らす
 *   - 手番強調: 手番側の色をわずかに明るく / 決着時は勝者色を強める
 *
 * 座標系は worldFromCell と同じ: row 0 側が -z（先手ブルー）、
 * row size-1 側が +z（後手レッド）。描画（board.js）はここの値を
 * そのまま three.js のジオメトリ / マテリアルへ流し込むだけにする。
 */
import { boardSpan, baseSpan } from './zoneMath.js';

export const GLOW = {
  floorScale: 1.8,      // 床グロー1辺 = 盤スパン × この倍率（1.6〜2.0）
  floorClearance: 0.04, // 盤土台の底からどれだけ下に敷くか
  floorOpacity: 0.6,    // 床グローの基準不透明度（加算合成）
  stripOut: 0.5,        // ストリップが土台の縁から外へ広がる幅
  stripLift: 0.005,     // ストリップを土台の底よりわずかに上へ（埋没防止）
  stripOpacity: 0.75,   // ストリップの基準不透明度（加算合成）
  breathePeriod: 4000,  // 呼吸の周期 (ms)
  breatheAmp: 0.15,     // 呼吸の振幅（±15%）
  turnBoost: 1.2,       // 手番側の色の強調倍率（控えめ）
  winnerBoost: 1.5,     // 決着時: 勝者色の強調倍率
  loserDim: 0.65,       // 決着時: 敗者色の減光倍率
  emphasisSmoothMs: 500, // 手番/決着の強調が切り替わる際の遷移の時定数
};

/**
 * 床グロー用プレーンのスペック。盤スパンの floorScale 倍の正方形を
 * 盤土台の底（baseBottomY）より floorClearance だけ下に敷く。
 */
export function floorGlowSpec(size, opts = {}) {
  const {
    scale = GLOW.floorScale,
    baseBottomY = -0.7, // board.js: -TILE_H - 0.5
    clearance = GLOW.floorClearance,
  } = opts;
  const span = boardSpan(size, opts) * scale;
  return { span, y: baseBottomY - clearance };
}

/**
 * 盤土台の底辺 4 辺に沿う発光ストリップのスペック。
 * 各要素 { side, x, z, w, d, y }（w = x方向, d = z方向の寸法）。
 * 前後（north = row 0 側 / south = row size-1 側）は土台の幅 + 両角ぶん、
 * 左右（west / east）は角の二重加算を避けて土台の奥行きだけ。
 */
export function stripSpecs(size, opts = {}) {
  const {
    out = GLOW.stripOut,
    baseBottomY = -0.7,
    lift = GLOW.stripLift,
  } = opts;
  const base = baseSpan(size, opts);
  const y = baseBottomY + lift;
  return [
    { side: 'north', x: 0, z: -(base.z + out) / 2, w: base.x + 2 * out, d: out, y },
    { side: 'south', x: 0, z: (base.z + out) / 2, w: base.x + 2 * out, d: out, y },
    { side: 'west', x: -(base.x + out) / 2, z: 0, w: out, d: base.z, y },
    { side: 'east', x: (base.x + out) / 2, z: 0, w: out, d: base.z, y },
  ];
}

/**
 * 呼吸カーブ。t=0 で 1（中間輝度）から始まる sin 波。
 * 戻り値は [1-amp, 1+amp] の輝度倍率。reduced-motion では呼び出し側が
 * t を進めず 1（= breathe(0)）で固定する。
 */
export function breathe(tMs, opts = {}) {
  const { period = GLOW.breathePeriod, amp = GLOW.breatheAmp } = opts;
  return 1 + amp * Math.sin(((tMs % period) / period) * Math.PI * 2);
}

/**
 * 陣地色ごとの強調倍率。side = 0（ブルー）| 1（レッド）。
 * 決着時（winner が 0|1）は勝者を明るく・敗者を暗く。
 * 対局中は手番（turn）側をわずかに明るく。それ以外は 1。
 */
export function glowSideFactor(side, opts = {}) {
  const {
    turn = null,
    winner = null,
    turnBoost = GLOW.turnBoost,
    winnerBoost = GLOW.winnerBoost,
    loserDim = GLOW.loserDim,
  } = opts;
  if (winner === 0 || winner === 1) {
    return side === winner ? winnerBoost : loserDim;
  }
  if (turn === side) return turnBoost;
  return 1;
}

/** 不透明度の合成（基準 × 呼吸 × 側強調）を [0, 1] へクランプ。 */
export function glowOpacity(base, breatheK, sideK) {
  return Math.min(1, Math.max(0, base * breatheK * sideK));
}

/**
 * 側強調倍率（glowSideFactor の結果）を現在値から目標値へ指数的に近づける。
 * 手番交代・決着の瞬間に側強調が瞬時に切り替わると、呼吸アニメーションが
 * 途中でリセットされたように見えてしまうため、なめらかに遷移させる。
 * dtMs が null/undefined または 0 以下のときは即座に目標値へスナップする
 * （reduced-motion や初期化時）。
 */
export function smoothFactor(current, target, dtMs, opts = {}) {
  const { tau = GLOW.emphasisSmoothMs } = opts;
  if (!(dtMs > 0)) return target;
  const alpha = 1 - Math.exp(-dtMs / tau);
  return current + (target - current) * alpha;
}
